import { type Member } from '@/features/iam/members/Member'
import type { AlertRetryWrapperFactory } from '@/features/shell'
import { action, computed, observable } from 'mobx'
import {
  EMPTY,
  type Observable,
  Subject,
  combineLatest,
  firstValueFrom,
} from 'rxjs'
import {
  catchError,
  distinctUntilChanged,
  filter,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators'
import { CursorPaged, type CursorPageQuery } from '@/infra/paging'
import { fromStream, toStream } from '@/utils/mobx-util'
import {
  fromAbortSignal,
  shareLatest,
  whileActive,
  withAbortSignal,
} from '@/utils/rx-util'
import { SearchTextViewModel } from '@/infra/SearchTextViewModel'
import { type ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { type OwnerSelected } from './DraftPersister'
import { type DraftStateSlice, type PrepareContext } from './DraftJobViewModel'
import { type StepFactory, type StepViewModel } from './OutlineViewModel'
import type { FetchMemberFn, SearchMembersFn } from '@/features/iam'
import { InfiniteScrollProvider } from '@/infra/InfiniteScrollProvider'
import { type OwnerScopedInput } from './PrefilledDraftInput'

const TITLE = 'Who would you like to submit this job for?'

/**
 * Owner Selector view model, for selecting the job owner
 */
export class OwnerSelectorViewModel
  implements ActivationLifecycle, DraftStateSlice
{
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Whether `prepare` has completed since activation.
   */
  @observable private accessor _isReady = false

  private readonly _isReady$ = toStream(() => this._isReady, true)

  /**
   * The singleton step for this slice.
   */
  readonly step: StepViewModel

  /**
   * Changes subject.
   * @private
   */
  private readonly _changesSubject = new Subject<OwnerSelected>()

  /**
   * Stream for whether the step is valid.
   */
  readonly isValid$ = toStream(() => this.isValid, true)

  /**
   * The search text.
   */
  readonly searchText = new SearchTextViewModel()

  /**
   * Changes stream.
   */
  readonly changes$ = this._changesSubject.asObservable()

  /**
   * The selected owner.
   */
  @observable private accessor _selectedOwner: Member | null = null

  /**
   * Scoped to owner inputs.
   */
  @observable accessor ownerScopedInput: OwnerScopedInput | undefined

  /**
   * Emits the selected owner on subscribe, but only after `prepare` has been run.
   */
  readonly selectedOwner$ = toStream(() => this.selectedOwner, true).pipe(
    distinctUntilChanged(),
    shareLatest(),
    // Don't start emitting until the slice is ready.
    whileActive(this._isReady$),
  )

  /**
   * Streams as MobX Observables.
   */
  private readonly _$

  /**
   * The scroll provider for Members.
   */
  readonly scroller = new InfiniteScrollProvider<Member>({
    limit: 20,
    fetchItems: async ({ paging, signal }) =>
      this._createAlertRetryWrapper(
        'Loading members failed',
        signal,
      )(() =>
        this._listMembers(this._$.selectedWorkspaceMember(), paging, signal),
      ),
  })

  /**
   * Constructor.
   *
   * @param createStep Factory for creating a Step.
   * @param selectedWorkspaceMember$ Observable for the selected workspace member
   * @param _isDisabled Getter for whether the selector is disabled
   * @param _canEditDraftJobsAsAdmin Getter for whether the selector is visible
   */
  constructor(
    createStep: StepFactory,
    private readonly _selectedWorkspaceMember$: Observable<Member | null>,
    private readonly _fetchMember: FetchMemberFn,
    private readonly _searchMembers: SearchMembersFn,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
    private readonly _isDisabled: () => boolean,
    private readonly _canEditDraftJobsAsAdmin: () => boolean,
  ) {
    this._$ = {
      selectedWorkspaceMember: fromStream(null, this._selectedWorkspaceMember$),
    }

    this._lifecycle.manage(this.selectedOwner$)

    this._lifecycle.manage(
      combineLatest([
        toStream(() => this._$.selectedWorkspaceMember(), true),
        toStream(() => this.selectedOwner, true),
      ]).pipe(
        filter(([, owner]) => owner === null),
        switchMap(() =>
          withAbortSignal((signal) => this.scroller.fetch({ signal })).pipe(
            catchError(() => EMPTY),
          ),
        ),
        whileActive(this._isReady$),
      ),
    )

    this._lifecycle.manage(
      this.searchText.debouncedValue$.pipe(
        switchMap(() =>
          withAbortSignal((signal) => this.scroller.fetch({ signal })).pipe(
            catchError(() => EMPTY),
          ),
        ),
        whileActive(this._isReady$),
      ),
    )

    // Re-apply the default owner on WS change, since whether
    // the user may pick an owner depends on the workspace.
    this._lifecycle.manage(
      this._selectedWorkspaceMember$.pipe(
        tap(this._selectInitialOwner),
        whileActive(this._isReady$),
      ),
    )

    this.step = createStep({
      stepId: 'owner_selection',
      isCompleted: () => Boolean(this.selectedOwner),
      outlineItemDisplay: () => {
        if (!this.selectedOwner) {
          return { title: TITLE }
        }

        return {
          title: TITLE,
          value: this.selectedOwner.displayName,
          visual: {
            type: 'Image',
            url: this.selectedOwner.avatar,
          },
        }
      },
    })
  }

  /**
   * Whether the step is valid.
   */
  @computed
  get isValid() {
    return Boolean(this.selectedOwner)
  }

  /**
   * This is the only step accessor.
   */
  @computed
  get stepAccessors() {
    return this._canEditDraftJobsAsAdmin() ? [this] : []
  }

  /**
   * Whether the owner selector is disabled.
   */
  @computed
  get disabled() {
    return this._isDisabled() || Boolean(this.ownerScopedInput)
  }

  /**
   * The currently selected owner.
   * `null` if the owner does not belong to the selected workspace.
   */
  @computed
  get selectedOwner() {
    const owner = this._selectedOwner
    if (!owner) {
      return null
    }

    if (
      owner.workspace.id === this._$.selectedWorkspaceMember()?.workspace.id
    ) {
      return owner
    }

    return null
  }

  /**
   * Members to show in the dropdown, with the current user surfaced
   * first when not searching.
   */
  @computed
  get filteredMembers(): readonly Member[] {
    const members = this.scroller.items
    const currentMember = this._$.selectedWorkspaceMember()

    if (this.searchText.value || !currentMember) {
      return members
    }

    return [
      currentMember,
      ...members.filter((m) => m.user.id !== currentMember.user.id),
    ]
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  @action.bound
  deactivate() {
    this._isReady = false
    this._lifecycle.deactivate()
  }

  /** @inheritDoc */
  @action.bound
  ready() {
    this._isReady = this._lifecycle.active
  }

  /**
   * Hydrates the Owner Selector state from the Job.
   *
   * @param context
   * @param signal
   */
  async prepare(context: PrepareContext, signal?: AbortSignal) {
    // Wait for the WS selector to have been prepared.
    await firstValueFrom(
      this._selectedWorkspaceMember$.pipe(takeUntil(fromAbortSignal(signal))),
      { defaultValue: null },
    )

    signal?.throwIfAborted()

    if (context.type === 'EditDraft') {
      this.setOwner(context.job.owner)
      return
    }

    // Must clear or set any owner scoped inputs before further preparation
    this.setOwnerScopeInput(context.prefilledDraft.ownerScopedInput)

    const prefilledUserPublicId =
      context.prefilledDraft.userPublicId ||
      context.prefilledDraft.ownerScopedInput?.publicId

    if (!prefilledUserPublicId) {
      this._selectInitialOwner()
      return
    }

    const owner = await this._fetchMember(
      {
        workspaceId: this._$.selectedWorkspaceMember()?.workspace.id,
        userPublicId: prefilledUserPublicId,
      },
      signal,
    )
    signal?.throwIfAborted()
    this.selectOwner(owner)
  }

  /**
   * Sets whether component should be scoped to job owner inputs.
   *
   * @param scopedInput
   */
  @action.bound
  setOwnerScopeInput(scopedInput?: OwnerScopedInput) {
    this.ownerScopedInput = scopedInput
  }

  /**
   * Sets the selected owner.
   *
   * @param owner
   */
  @action.bound
  setOwner(owner: Member | null) {
    this._selectedOwner = owner
  }

  /**
   * Selects the specified owner.
   *
   * @param owner
   */
  @action.bound
  selectOwner(owner: Member | undefined | null) {
    owner ??= null

    if (owner !== this._selectedOwner) {
      this.setOwner(owner)
      this._changesSubject.next({ owner, type: 'OwnerSelected' })
    }
  }

  /**
   * Triggers new member search.
   *
   * @param newText
   */
  @action.bound
  triggerSearch(newText: string) {
    this.searchText.change(newText)
  }

  /**
   * Used to select the initial owner, if any.
   * Only happens if we don't have a selection, and only
   * when the user cannot pick an owner themselves.
   *
   * @private
   */
  @action.bound
  private _selectInitialOwner() {
    if (this.disabled) {
      return
    }

    if (this.selectedOwner) {
      return
    }

    if (!this._canEditDraftJobsAsAdmin()) {
      this.selectOwner(this._$.selectedWorkspaceMember())
    }
  }

  /**
   * Lists the workspace's members.
   *
   * @private
   */
  private async _listMembers(
    member: Member | null,
    paging: CursorPageQuery,
    signal?: AbortSignal,
  ) {
    if (!member) {
      return CursorPaged.empty<Member>()
    }

    if (
      !member.permissions.adminUserList &&
      !member.permissions.adminClientUserList
    ) {
      return CursorPaged.of([member])
    }

    return this._searchMembers(
      {
        ...paging,
        userTypes:
          !member.permissions.adminUserList &&
          member.permissions.adminClientUserList
            ? ['User']
            : undefined,
        workspaceId: member.workspace.id,
        searchText: this.searchText.value,
      },
      signal,
    )
  }
}

/**
 * Factory for creating an {@link OwnerSelectorViewModel}.
 */
export type OwnerSelectorViewModelFactory = ReturnType<
  typeof createOwnerSelectorViewModelFactory
>

/** @see OwnerSelectorViewModelFactory */
export function createOwnerSelectorViewModelFactory(
  fetchMember: FetchMemberFn,
  searchMembers: SearchMembersFn,
  createAlertRetryWrapper: AlertRetryWrapperFactory,
) {
  return (
    createStep: StepFactory,
    selectedWorkspaceMember$: Observable<Member | null>,
    isDisabled: () => boolean,
    canEditDraftJobsAsAdmin: () => boolean,
  ) =>
    new OwnerSelectorViewModel(
      createStep,
      selectedWorkspaceMember$,
      fetchMember,
      searchMembers,
      createAlertRetryWrapper,
      isDisabled,
      canEditDraftJobsAsAdmin,
    )
}
